import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid } from '@breathelife/mui';
import { Language, LineOfBusinessName, Permission, Questionnaire } from '@breathelife/types';
import { ApplicationMode, ApplicationModes } from '@zinnia/form-engine-sdk';

import { SubmitButton } from '../../../Components/Button/SubmitButton';
import RadioGroup, { Radio } from '../../../Components/RadioGroup/RadioGroup';
import { RestrictedToUserPermissions } from '../../../Components/Restricted/RestrictedToUserPermissions';
import Typography from '../../../Components/Typography';
import { useLanguage } from '../../../Hooks';
import { ModalLayout } from '../../../Layouts/Modal/ModalLayout';

type LaunchQuestionnaireData = {
  mode: ApplicationMode;
  lineOfBusiness: LineOfBusinessName;
  questionnaireId: string;
  communicationLanguage: Language;
};

type LaunchApplicationModalProps = {
  onSubmitClick: (data: LaunchQuestionnaireData) => void;
  isOpen: boolean;
  closeModal: () => void;
  disableSubmitButton: boolean;
  questionnaires: Questionnaire[];
  applicationModes: ApplicationMode[];
  languageChoices: Language[];
};

export function LaunchApplicationModal(props: LaunchApplicationModalProps): ReactElement {
  const { isOpen, closeModal, disableSubmitButton, onSubmitClick, questionnaires, applicationModes, languageChoices } =
    props;
  const { t } = useTranslation();
  const language = useLanguage();

  const [selectedApplicationMode, setSelectedApplicationMode] = useState<ApplicationMode>(ApplicationModes.digital);

  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<string | undefined>(undefined);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<LineOfBusinessName | undefined>(undefined);

  const [selectedLanguage, setSelectedLanguage] = useState<Language | undefined>(undefined);

  const lineOfBusinessOptions: { label: string; value: LineOfBusinessName }[] = useMemo(() => {
    const lobOptions = questionnaires.reduce((acc: { label: string; value: LineOfBusinessName }[], element) => {
      element.linesOfBusiness?.forEach((lineOfBusiness) => {
        const label = lineOfBusiness.name[language];
        const exists = !!acc.find((val) => val.label === label);
        if (label && !exists) {
          acc.push({
            label,
            value: lineOfBusiness.tag,
          });
        }
      });
      return acc;
    }, []);
    return lobOptions;
  }, [questionnaires, language]);

  const questionnairesOptions: { label: string; value: string }[] = useMemo(() => {
    if (!selectedLineOfBusiness) {
      return [];
    }
    return questionnaires.reduce((acc: { label: string; value: string }[], questionnaire) => {
      const hasSelectedLineOfBusiness = questionnaire.linesOfBusiness.find(
        (lineOfBusiness) => lineOfBusiness.tag === selectedLineOfBusiness,
      );
      const label = questionnaire.name[language];
      if (hasSelectedLineOfBusiness && label) {
        acc.push({ label, value: questionnaire.id });
      }
      return acc;
    }, []);
  }, [selectedLineOfBusiness, questionnaires, language]);

  useEffect(() => {
    if (!selectedLanguage && languageChoices.length === 1) {
      setSelectedLanguage(languageChoices[0]);
    }
    if (!isOpen) {
      setSelectedLineOfBusiness(undefined);
      if (languageChoices.length > 1) {
        setSelectedLanguage(undefined);
      }
    }
  }, [isOpen, languageChoices, selectedLanguage]);

  useEffect(() => {
    if (questionnairesOptions.length === 1) {
      setSelectedQuestionnaireId(questionnairesOptions[0].value);
    } else {
      setSelectedQuestionnaireId(undefined);
    }
  }, [questionnairesOptions]);

  const isValid = useMemo(() => {
    if (
      !selectedLanguage ||
      !selectedLineOfBusiness ||
      !selectedApplicationMode ||
      (!selectedQuestionnaireId && questionnaires.length !== 1)
    ) {
      return false;
    }
    return true;
  }, [
    questionnaires.length,
    selectedApplicationMode,
    selectedLineOfBusiness,
    selectedQuestionnaireId,
    selectedLanguage,
  ]);

  const handleSubmit = useCallback(() => {
    if (!selectedLineOfBusiness || !selectedApplicationMode || !selectedQuestionnaireId || !selectedLanguage) {
      return;
    }

    onSubmitClick({
      mode: selectedApplicationMode,
      questionnaireId: selectedQuestionnaireId,
      lineOfBusiness: selectedLineOfBusiness,
      communicationLanguage: selectedLanguage,
    });
  }, [selectedLineOfBusiness, selectedQuestionnaireId, selectedApplicationMode, selectedLanguage, onSubmitClick]);

  return (
    <ModalLayout
      maxWidth='sm'
      isOpen={isOpen}
      closeModal={closeModal}
      title={t('modals.createButton.title')}
      submitButton={
        <RestrictedToUserPermissions requiredPermissions={[Permission.LeadCreate, Permission.ApplicationLaunchMe]}>
          <SubmitButton
            onClick={handleSubmit}
            disabled={disableSubmitButton || !isValid}
            data-testid='launchApplicationFromModal'
          >
            {t('cta.launchApplication')}
          </SubmitButton>
        </RestrictedToUserPermissions>
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant='h3' grey={90}>
            {t('modals.createButton.lineOfBusiness')}
          </Typography>

          <RadioGroup
            label={t('modals.createButton.lineOfBusinessLabel')}
            name='createButton-lineOfBusiness'
            onChange={setSelectedLineOfBusiness}
            value={selectedLineOfBusiness}
          >
            <Grid container spacing={1}>
              {lineOfBusinessOptions.map((choice) => {
                return (
                  <Grid item sm={6} key={choice.value}>
                    <Radio value={choice.value} label={choice.label} />
                  </Grid>
                );
              })}
            </Grid>
          </RadioGroup>
        </Grid>

        {languageChoices.length > 1 && (
          <Grid item xs={12}>
            <Typography variant='h3' grey={90}>
              {t('modals.createButton.languageOfCorrespondence')}
            </Typography>

            <RadioGroup
              label={t('modals.createButton.languageOfCorrespondenceLabel')}
              name='createButton-languageOfCorrespondence'
              onChange={setSelectedLanguage}
              value={selectedLanguage}
            >
              <Grid container spacing={1}>
                {languageChoices.map((lang) => {
                  return (
                    <Grid item sm={6} key={lang}>
                      <Radio
                        value={lang}
                        label={t(`modals.createButton.languageOfCorrespondenceChoices.${Language[lang]}`)}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </RadioGroup>
          </Grid>
        )}

        {questionnairesOptions.length > 1 && (
          <Grid item xs={12}>
            <Typography variant='h3' grey={90}>
              {t('modals.createButton.formType')}
            </Typography>

            <RadioGroup
              label={t('modals.createButton.formTypeLabel')}
              name='createButton-form-type'
              onChange={setSelectedQuestionnaireId}
              value={selectedQuestionnaireId}
            >
              <Grid container spacing={1}>
                {questionnairesOptions.map((choice) => {
                  return (
                    <Grid item sm={6} key={choice.value}>
                      <Radio value={choice.value} label={choice.label} />
                    </Grid>
                  );
                })}
              </Grid>
            </RadioGroup>
          </Grid>
        )}

        {applicationModes.length > 1 && (
          <Grid item xs={12}>
            <Typography variant='h3' grey={90}>
              {t('modals.createButton.applicationMode')}
            </Typography>

            <RadioGroup<ApplicationMode>
              label={t('modals.createButton.applicationModeLabel')}
              name='createButton-applicationMode'
              onChange={(value) => setSelectedApplicationMode(value)}
              value={selectedApplicationMode}
            >
              <Grid container spacing={1}>
                {applicationModes.map((choice) => {
                  return (
                    <Grid item sm={6} key={choice}>
                      <Radio<ApplicationMode> value={choice} label={t(`applicationModes.${choice}`)} />
                    </Grid>
                  );
                })}
              </Grid>
            </RadioGroup>
          </Grid>
        )}
      </Grid>
    </ModalLayout>
  );
}
